import { getAppMeta } from "@fieldbook/common/authClaims"
import { supabase } from "@fieldbook/common/supabase"
import { isAuthRetryableFetchError } from "@supabase/supabase-js"
import type { Session, User } from "@supabase/supabase-js"
import { authStorage } from "@/platform"
import { TIMED_OUT, withTimeout } from "@/lib/withTimeout"

const OFFLINE_AUTH_SNAPSHOT_KEY = "fieldbook.offlineAuthSnapshot"
const OFFLINE_ACCESS_DAYS = 30
const AUTH_STORAGE_TIMEOUT_MS = 5_000

export type OfflineAuthSnapshot = {
  userId: string
  email?: string
  orgId?: string
  orgName?: string
  role?: string
  lastSuccessfulLoginAt: string
  lastSuccessfulSessionRefreshAt?: string
  offlineAccessUntil: string
}

type AuthSessionResult = {
  session: Session | null
  offline: boolean
}

const isOnline = () =>
  typeof navigator === "undefined" ? true : navigator.onLine

const addDays = (date: Date, days: number) => {
  const next = new Date(date)
  next.setUTCDate(next.getUTCDate() + days)
  return next
}

const isOfflineAuthSnapshot = (
  snapshot: unknown,
): snapshot is OfflineAuthSnapshot => {
  if (!snapshot || typeof snapshot !== "object") return false
  const { userId, lastSuccessfulLoginAt, offlineAccessUntil } =
    snapshot as Record<string, unknown>
  return (
    typeof userId === "string" &&
    userId.length > 0 &&
    typeof lastSuccessfulLoginAt === "string" &&
    !Number.isNaN(Date.parse(lastSuccessfulLoginAt)) &&
    typeof offlineAccessUntil === "string" &&
    !Number.isNaN(Date.parse(offlineAccessUntil))
  )
}

const isOfflineSnapshotValid = (snapshot: OfflineAuthSnapshot) =>
  Date.parse(snapshot.offlineAccessUntil) > Date.now()

const encodeBase64Url = (value: unknown) =>
  btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(value))))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "")

// Local-only claims shim for useAuth/getAppMeta while offline. Supabase-js
// keeps its own internal session for network auth; do not send this token.
const createOfflineJwt = (snapshot: OfflineAuthSnapshot) =>
  [
    encodeBase64Url({ alg: "none", typ: "JWT" }),
    encodeBase64Url({
      sub: snapshot.userId,
      app_metadata: {
        org_id: snapshot.orgId,
        org_name: snapshot.orgName,
        role: snapshot.role,
      },
    }),
    "offline",
  ].join(".")

const createOfflineSession = (snapshot: OfflineAuthSnapshot): Session => {
  const user: User = {
    id: snapshot.userId,
    email: snapshot.email,
    created_at: snapshot.lastSuccessfulLoginAt,
    app_metadata: {},
    user_metadata: {},
    aud: "authenticated",
  }

  return {
    access_token: createOfflineJwt(snapshot),
    refresh_token: "",
    expires_in: 0,
    token_type: "bearer",
    user,
    // This is a local identity shim, not a bearer credential. It is only
    // usable during the cached offline-access window.
    expires_at: Math.floor(Date.parse(snapshot.offlineAccessUntil) / 1000),
  }
}

export const isOfflineSession = (session: Session | null | undefined) =>
  session?.refresh_token === "" && session.expires_in === 0

export const readOfflineAuthSnapshot =
  async (): Promise<OfflineAuthSnapshot | null> => {
    let raw: string | null
    try {
      const result = await withTimeout(
        Promise.resolve(authStorage.getItem(OFFLINE_AUTH_SNAPSHOT_KEY)),
        AUTH_STORAGE_TIMEOUT_MS,
      )
      if (result === TIMED_OUT) return null
      raw = result
    } catch {
      // Auth storage is local but still async on native. A storage failure
      // must not discard a valid snapshot that may be readable next time.
      return null
    }
    if (!raw) return null

    try {
      const snapshot: unknown = JSON.parse(raw)
      if (isOfflineAuthSnapshot(snapshot)) return snapshot
    } catch {
      // Malformed persisted data is removed below.
    }

    // A malformed snapshot cannot establish identity. Its removal is
    // best-effort so a failing storage backend never blocks startup.
    void Promise.resolve(
      authStorage.removeItem(OFFLINE_AUTH_SNAPSHOT_KEY),
    ).catch(() => {})
    return null
  }

export const writeOfflineAuthSnapshot = async (
  session: Session,
  timestamp = new Date(),
) => {
  const appMeta = getAppMeta(session)
  const existing = await readOfflineAuthSnapshot()
  const snapshot: OfflineAuthSnapshot = {
    userId: session.user.id,
    email: session.user.email,
    orgId: appMeta.org_id,
    orgName: appMeta.org_name,
    role: appMeta.role,
    lastSuccessfulLoginAt:
      existing?.lastSuccessfulLoginAt ?? timestamp.toISOString(),
    lastSuccessfulSessionRefreshAt: timestamp.toISOString(),
    offlineAccessUntil: addDays(timestamp, OFFLINE_ACCESS_DAYS).toISOString(),
  }

  await withTimeout(
    Promise.resolve(
      authStorage.setItem(OFFLINE_AUTH_SNAPSHOT_KEY, JSON.stringify(snapshot)),
    ),
    AUTH_STORAGE_TIMEOUT_MS,
    () => undefined,
  )
}

const removeOfflineAuthSnapshot = () =>
  Promise.resolve().then(() =>
    authStorage.removeItem(OFFLINE_AUTH_SNAPSHOT_KEY),
  )

// Logout must wait for the storage backend to confirm deletion. Resolving
// early would leave the offline snapshot available on a later cold start.
export const clearOfflineAuthSnapshot = () => removeOfflineAuthSnapshot()

// Startup cleanup must never hold the route gate. It is intentionally separate
// from the logout path above because a timeout is only acceptable here.
export const clearOfflineAuthSnapshotBestEffort = async () => {
  try {
    await withTimeout(
      removeOfflineAuthSnapshot(),
      AUTH_STORAGE_TIMEOUT_MS,
      () => undefined,
    )
  } catch {
    // A later successful login can replace the stale snapshot. Do not block
    // route rendering on storage cleanup after a confirmed no-session result.
  }
}

const SESSION_LOOKUP_TIMEOUT_MS = 5_000

const getOfflineSession = async (): Promise<Session | null> => {
  const snapshot = await readOfflineAuthSnapshot()
  return snapshot && isOfflineSnapshotValid(snapshot)
    ? createOfflineSession(snapshot)
    : null
}

const canUseOfflineSnapshot = (error: unknown) =>
  !isOnline() ||
  (error ? isAuthRetryableFetchError(error) || error === TIMED_OUT : false)

const resolveSessionWithOfflineFallback =
  async (): Promise<AuthSessionResult> => {
    if (!isOnline()) {
      const session = await getOfflineSession()
      if (session) return { session, offline: true }
      // A valid persisted Supabase token does not need the network. This
      // covers users who logged in before offline snapshots were introduced
      // or whose snapshot was legitimately unavailable.
    }

    let session: Session | null = null
    let error: unknown = null
    try {
      const sessionLookup = supabase.auth.getSession()
      sessionLookup.catch(() => {
        // The timeout may win before Supabase resolves; consume a later
        // rejection so a stalled network lookup cannot become unhandled.
      })
      const result = await withTimeout(sessionLookup, SESSION_LOOKUP_TIMEOUT_MS)
      if (result === TIMED_OUT) throw TIMED_OUT
      session = result.data.session
      error = result.error
    } catch (err) {
      error = err
    }

    if (session) {
      try {
        await writeOfflineAuthSnapshot(session)
      } catch {
        // A live session remains valid even if its offline snapshot cannot
        // be updated on this device.
      }
      return { session, offline: false }
    }

    if (canUseOfflineSnapshot(error)) {
      const offlineSession = await getOfflineSession()
      return { session: offlineSession, offline: Boolean(offlineSession) }
    }

    if (isOnline()) {
      try {
        const hadSnapshot = Boolean(await readOfflineAuthSnapshot())
        void clearOfflineAuthSnapshotBestEffort()
        if (hadSnapshot) await supabase.auth.signOut({ scope: "local" })
      } catch {
        // A confirmed online no-session result still redirects to login even if
        // best-effort local cleanup cannot complete.
      }
    }

    return { session: null, offline: false }
  }

export const getSessionWithOfflineFallback = resolveSessionWithOfflineFallback
